import axios, { getData } from "../../../axios";
import { createSlice } from "@reduxjs/toolkit";
import {
  Team,
  teamInputT,
  teamT,
  teamDriversT,
  teamVehiclesT,
  CargoType,
  CargoQuality,
  TrailerQuality,
  cargoTypeT,
  cargoQualityT,
  trailerQualityT,
  EconomicProductGroup,
  economicProductGroupsT,
} from "dora-contracts";
import { createErrorReportingAsyncThunk } from "../../helpers";
import * as t from "io-ts";
import * as tPromise from "io-ts-promise";
import { isRight } from "fp-ts/Either";

// Decode teams with a loose cargoTypes array (plain strings) so unknown cargo
// types coming from a newer server don't fail the whole decode — the client
// may be on older code than the server before a refresh. Unknown cargo types
// are filtered out below, mirroring the mobile-app cargo-types loader.
const looseTeamsT = t.array(
  t.intersection([
    t.strict({ id: t.string }),
    teamInputT,
    t.strict({
      cargoTypes: t.array(t.string),
      cargoQualities: t.array(cargoQualityT),
      cargoRequirements: t.array(trailerQualityT),
    }),
  ])
);

type TeamInput = t.TypeOf<typeof teamInputT>;

type State = {
  teams: Team[];
  teamsDrivers: Record<string, string[]>;
  teamsVehicles: Record<string, string[]>;
  productGroups: EconomicProductGroup[];
};

const initialState: State = { teams: [], teamsDrivers: {}, teamsVehicles: {}, productGroups: [] };

const prefix = "data/teams";

export const addTeam = createErrorReportingAsyncThunk(
  `${prefix}/add-team`,
  async (team: TeamInput): Promise<Team> => {
    return axios
      .post("/api/teams", team)
      .then(getData)
      .then(tPromise.decode(teamT));
  }
);

export const updateTeam = createErrorReportingAsyncThunk(
  `${prefix}/update-team`,
  async ({
    teamId,
    ...data
  }: TeamInput & { teamId: string }): Promise<Team> => {
    return axios
      .put(`/api/teams/${teamId}`, data)
      .then(getData)
      .then(tPromise.decode(teamT));
  }
);

export const loadTeams = createErrorReportingAsyncThunk(
  `${prefix}/load-teams`,
  async (_): Promise<Team[]> => {
    const teams = await axios
      .get("/api/teams")
      .then(getData)
      .then(tPromise.decode(looseTeamsT));

    // Filter out cargo types the client doesn't know about so they don't
    // crash the decode (server may send values added after the client built).
    return teams.map((team) => ({
      ...team,
      cargoTypes: team.cargoTypes.filter((cargoType) =>
        isRight(cargoTypeT.decode(cargoType))
      ) as CargoType[],
    }));
  }
);

export const loadTeamDrivers = createErrorReportingAsyncThunk(
  `${prefix}/load-team-drivers`,
  async (teamId: string) => {
    return axios
      .get(`/api/teams/${teamId}/drivers`)
      .then(getData)
      .then(tPromise.decode(teamDriversT));
  }
);

export const loadTeamVehicles = createErrorReportingAsyncThunk(
  `${prefix}/load-team-vehicles`,
  async (teamId: string) => {
    return axios
      .get(`/api/teams/${teamId}/vehicles`)
      .then(getData)
      .then(tPromise.decode(teamVehiclesT));
  }
);

export const attachVehicleToTeam = createErrorReportingAsyncThunk(
  `${prefix}/attach-vehicle-to-team`,
  async (
    { teamId, vehicleId }: { teamId: string; vehicleId: string },
    getThunkAPI
  ) => {
    await axios.post(`/api/teams/${teamId}/vehicles/${vehicleId}`);
    const { dispatch } = getThunkAPI;
    dispatch(loadTeamVehicles(teamId));
  }
);

export const saveTeamCargoTypes = createErrorReportingAsyncThunk(
  `${prefix}/save-team-cargo-types`,
  async ({ teamId, cargoTypes }: { teamId: string; cargoTypes: CargoType[] }) =>
    axios
      .put(`/api/teams/${teamId}/cargo-types`, { cargoTypes })
      .then(getData)
      .then(tPromise.decode(teamT))
);

export const saveTeamCargoQualities = createErrorReportingAsyncThunk(
  `${prefix}/save-team-cargo-qualities`,
  async ({
    teamId,
    cargoQualities,
  }: {
    teamId: string;
    cargoQualities: CargoQuality[];
  }) =>
    axios
      .put(`/api/teams/${teamId}/cargo-qualities`, { cargoQualities })
      .then(getData)
      .then(tPromise.decode(teamT))
);

export const saveTeamCargoRequirements = createErrorReportingAsyncThunk(
  `${prefix}/save-team-trailer-qualities`,
  async ({
    teamId,
    cargoRequirements,
  }: {
    teamId: string;
    cargoRequirements: TrailerQuality[];
  }) =>
    axios
      .put(`/api/teams/${teamId}/cargo-requirements`, { cargoRequirements })
      .then(getData)
      .then(tPromise.decode(teamT))
);

export const detachVehicleFromTeam = createErrorReportingAsyncThunk(
  `${prefix}/detach-vehicle-from-team`,
  async (
    { teamId, vehicleId }: { teamId: string; vehicleId: string },
    getThunkAPI
  ) => {
    await axios.delete(`/api/teams/${teamId}/vehicles/${vehicleId}`);
    const { dispatch } = getThunkAPI;
    dispatch(loadTeamVehicles(teamId));
  }
);

export const attachDriverToTeam = createErrorReportingAsyncThunk(
  `${prefix}/attach-driver-to-team`,
  async (
    { teamId, driverId }: { teamId: string; driverId: string },
    getThunkAPI
  ) => {
    await axios.post(`/api/teams/${teamId}/drivers/${driverId}`);
    const { dispatch } = getThunkAPI;
    dispatch(loadTeamDrivers(teamId));
  }
);

export const detachDriverFromTeam = createErrorReportingAsyncThunk(
  `${prefix}/detach-driver-from-team`,
  async (
    { teamId, driverId }: { teamId: string; driverId: string },
    getThunkAPI
  ) => {
    await axios.delete(`/api/teams/${teamId}/drivers/${driverId}`);
    const { dispatch } = getThunkAPI;
    dispatch(loadTeamDrivers(teamId));
  }
);

export const loadProductGroups = createErrorReportingAsyncThunk(
  `${prefix}/load-product-groups`,
  async () => {
    return axios
      .get("/api/e-conomic/product-groups")
      .then(getData)
      .then(tPromise.decode(economicProductGroupsT));
  }
);

export const attachProductGroupToTeam = createErrorReportingAsyncThunk(
  `${prefix}/attach-product-group-to-team`,
  async (
    { teamId, productGroupId }: { teamId: string; productGroupId: string },
    getThunkAPI
  ) => {
    await axios.post(`/api/teams/${teamId}/product-groups/${productGroupId}`);
    getThunkAPI.dispatch(loadProductGroups());
  }
);

export const detachProductGroupFromTeam = createErrorReportingAsyncThunk(
  `${prefix}/detach-product-group-from-team`,
  async (
    { teamId, productGroupId }: { teamId: string; productGroupId: string },
    getThunkAPI
  ) => {
    await axios.delete(`/api/teams/${teamId}/product-groups/${productGroupId}`);
    getThunkAPI.dispatch(loadProductGroups());
  }
);

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(addTeam.fulfilled, (state, action) => {
        state.teams.push(action.payload);
      })
      .addCase(loadTeams.fulfilled, (state, action) => {
        state.teams = action.payload;
      })
      .addCase(loadTeamDrivers.fulfilled, (state, action) => {
        state.teamsDrivers[action.meta.arg] = action.payload.map(
          (td) => td.driverId
        );
      })
      .addCase(loadTeamVehicles.fulfilled, (state, action) => {
        state.teamsVehicles[action.meta.arg] = action.payload.map(
          (tv) => tv.trailerId
        );
      })
      .addCase(updateTeam.fulfilled, (state, action) => {
        const index = state.teams.findIndex(
          (t) => t.id === action.payload.id
        );
        if (index !== -1) {
          state.teams[index] = action.payload;
        }
      })
      .addCase(saveTeamCargoTypes.fulfilled, (state, action) => {
        const team = state.teams.find((t) => t.id === action.meta.arg.teamId);
        if (team) {
          team.cargoTypes = action.payload.cargoTypes;
        }
      })
      .addCase(saveTeamCargoQualities.fulfilled, (state, action) => {
        const team = state.teams.find((t) => t.id === action.meta.arg.teamId);
        if (team) {
          team.cargoQualities = action.payload.cargoQualities;
        }
      })
      .addCase(saveTeamCargoRequirements.fulfilled, (state, action) => {
        const team = state.teams.find((t) => t.id === action.meta.arg.teamId);
        if (team) {
          team.cargoRequirements = action.payload.cargoRequirements;
        }
      })
      .addCase(loadProductGroups.fulfilled, (state, action) => {
        state.productGroups = action.payload;
      });
  },
});

export default slice.reducer;
